import {
  Box,
  FormControlLabel,
  FormHelperText,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { AdFieldCurrencyEnum, RentPeriodEnum } from '../../types/enums';
import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';

import { FormInputNumeric, LabelComponent } from '../uiComponents';
import { AiReviewFieldNotice, useAiListingReview } from './AiListingReview';
import { EditorRequiredIndicator } from './EditorSection';
import { EditorSelectField } from './fields';
import styles from './PropertyAdEditor.module.scss';

const currencyOptions = [
  { label: 'AED', value: AdFieldCurrencyEnum.AED },
  { label: 'USD', value: AdFieldCurrencyEnum.USD },
] as const;

const rentalPriceOptions = [
  { label: 'Year', period: RentPeriodEnum.Yearly, priceField: 'priceYearly' },
  { label: 'Month', period: RentPeriodEnum.Monthly, priceField: 'priceMonthly' },
  { label: 'Week', period: RentPeriodEnum.Weekly, priceField: 'priceWeekly' },
  { label: 'Day', period: RentPeriodEnum.Daily, priceField: 'priceDaily' },
] as const;

export const RentPricingDetails = () => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<PropertyAdEditorFormValues>();
  const aiReview = useAiListingReview();
  const currency = useWatch({ control, name: 'currency' }) ?? AdFieldCurrencyEnum.AED;
  const defaultRentFrequency = useWatch({ control, name: 'defaultRentFrequency' });

  const handleRentalPriceChange = (
    priceField: (typeof rentalPriceOptions)[number]['priceField'],
    period: RentPeriodEnum,
    value: number | null
  ) => {
    setValue(priceField, value !== null && value > 0 ? value : null, { shouldDirty: true });
    aiReview?.resolveField(priceField);

    if (value !== null && value > 0 && !defaultRentFrequency) {
      setValue('defaultRentFrequency', period, { shouldDirty: true });
    }
  };

  return (
    <Box aria-label='Rent pricing' component='section' minWidth={0}>
      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          minWidth: 0,
          width: '100%',
        }}
      >
        <EditorSelectField label='Currency' name='currency' options={currencyOptions} required />

        <Box
          aria-labelledby='rental-prices-title'
          component='section'
          minWidth={0}
          sx={{ gridColumn: '1 / -1' }}
        >
          <Stack marginBottom={2} spacing={0.5}>
            <Typography color='#2a3842' component='h3' id='rental-prices-title' variant='h6'>
              Rental prices
              <EditorRequiredIndicator />
            </Typography>
            <Typography
              color='#6f7d87'
              component='p'
              fontWeight={400}
              lineHeight='22px'
              variant='body2'
            >
              Choose one default price. Other rental prices are optional.
            </Typography>
          </Stack>

          <Controller
            control={control}
            name='defaultRentFrequency'
            rules={{ required: 'Please fill in the rental period field' }}
            render={({ field }) => (
              <RadioGroup
                aria-label='Default rental price'
                name={field.name}
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  minWidth: 0,
                  width: '100%',
                }}
                value={field.value ?? ''}
                onBlur={field.onBlur}
                onChange={(event) => {
                  field.onChange(event);
                  aiReview?.resolveField('defaultRentFrequency');
                }}
              >
                {rentalPriceOptions.map((option) => (
                  <Paper
                    aria-label={`${option.label} rental price`}
                    component='article'
                    elevation={0}
                    key={option.period}
                    sx={{
                      backgroundColor: '#f9fcff',
                      border: '1px solid #dce9f7',
                      borderRadius: '14px',
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 184,
                      minWidth: 0,
                      padding: 2,
                    }}
                  >
                    <Typography
                      color='#1650ff'
                      component='h4'
                      fontSize={17}
                      marginBottom='14px'
                      variant='h6'
                    >
                      {option.label}
                    </Typography>
                    <Box className={styles.rentalPriceInput} minWidth={0} width='100%'>
                      <LabelComponent
                        isRequired={defaultRentFrequency === option.period}
                        label='Price'
                      />
                      <FormInputNumeric
                        name={option.priceField}
                        placeholder='Enter price'
                        suffix={` ${currency}`}
                        uiVariant='blank'
                        valueType='number'
                        handleChange={(value) =>
                          handleRentalPriceChange(option.priceField, option.period, value)
                        }
                      />
                      <AiReviewFieldNotice target={option.priceField} />
                    </Box>
                    <FormControlLabel
                      control={
                        <Radio
                          inputProps={{
                            'aria-label': `Default price for ${option.label}`,
                          }}
                        />
                      }
                      label='Default price'
                      sx={{
                        margin: 'auto 0 0',
                        minWidth: 0,
                        '& .MuiFormControlLabel-label': {
                          color: '#2a3842',
                          fontSize: 14,
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                        },
                      }}
                      value={option.period}
                    />
                  </Paper>
                ))}
              </RadioGroup>
            )}
          />
          {errors.defaultRentFrequency?.message && (
            <FormHelperText error>{errors.defaultRentFrequency.message}</FormHelperText>
          )}
          <AiReviewFieldNotice target='defaultRentFrequency' />
        </Box>
      </Box>
    </Box>
  );
};
